import { LIST_PAGE_SIZE } from '../../common/stores/Pager';
import { observable, action, computed, runInAction, reaction, autorun } from 'mobx';
import { ProjectMetadataVisualStore } from './ProjectMetadataVisualStore';
import { AdministrationService } from '../../project_management/services/AdministrationService';
import _ from 'lodash';
import ProjectsRootVisualStore from '../../common/stores/ProjectsRootVisualStore';
import { Package, PackageState } from '../../common/models';
import {
    PackagesResponse,
    SearchPackagesAutoCompleteSourceItem,
    PackageChange,
    PackageStateResult,
    OrderBy
} from '../../common/types';
import { ProjectsService } from '../../common/services';
import { ErrorStore } from '../../common/stores';
import { CellMeasurerCache } from 'react-virtualized';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { message } from 'antd';
import { PackageSource } from '../../common/models/PackageSource';
import { GlobalAdministrationService } from '../../administration/service/GlobalAdministrationService';
import { AlphaConfig } from '../../administration/models/AlphaConfig';
import { DefaultAlphaConfigurations } from '../../common/constants';
import { LocalStorageService } from '../../common/services';
import { ALPHA_CONFIG_VARIABLES } from '../../administration/types/AlphaConfigModel';

export const SEARCH_DEBOUNCE_TIME = 500;

// When modifiyng filters increase version number to prevent conflicts with old cached data
const packageFilterStorageVersion = 1;

type PackageFilterStorageValues = {
    pageSize: number;
    currentPage: number;
    searchTerm: string;
    currentTags: string[];
    currentState: string[];
    isPackageSourceChecked: boolean;
    isProtectedOnlyChecked: boolean;
    orderBy: OrderBy;
};

const defaultFilterValues = {
    pageSize: LIST_PAGE_SIZE,
    currentPage: 0,
    searchTerm: '',
    currentTags: [] as string[],
    currentState: [] as string[],
    isPackageSourceChecked: false,
    isProtectedOnlyChecked: false,
    orderBy: undefined
} as const;

type PackageFilterStorageItem = Partial<PackageFilterStorageValues>;

interface IsOperationInProcess {
    [key: string]: boolean;
}

export class UploadedPackagesVisualStore {
    @observable
    isLoading: boolean = false;

    @observable
    isReindexingInProcess: IsOperationInProcess = {};

    @observable
    isCleanupInProcess: IsOperationInProcess = {};

    @observable
    searchTerm: string = '';

    @observable
    filterQuery: string;

    @observable
    isPackageSourceChecked: boolean = false;

    @observable
    isProtectedOnlyChecked: boolean = false;

    @observable
    isReparseInProcess: IsOperationInProcess = {};

    @observable
    isStartingInterpretation: { [id: string]: boolean } = {};

    @observable
    isStartingPreparation: { [id: string]: boolean } = {};

    @observable
    isProcessingStatusUnknown: IsOperationInProcess = {};

    private processingRevisions = new Map<string, number>();
    private processingRows = new Map<string, Package>();
    private packageListRequest = 0;

    private advanceProcessingRevision(id: string) {
        this.processingRevisions.set(id, (this.processingRevisions.get(id) ?? 0) + 1);
        const row = this.currentProjectPackages.find(pkg => pkg.id === id);
        if (row) this.processingRows.set(id, row);
    }

    private isProcessingRequestPending(id: string) {
        return this.isStartingPreparation[id] || this.isStartingInterpretation[id] || this.isReindexingInProcess[id];
    }

    @observable
    currentTags: string[] = [];

    @observable
    currentState: string[] = [];

    @observable
    autocompleteSource: SearchPackagesAutoCompleteSourceItem[] = [];

    @observable
    uploadDocsDialogVisible: boolean = false;

    @observable
    selectedPackages: string[] = [];

    @observable
    defaultAlphaConfigs: AlphaConfig[] = [];

    @observable
    currentProjectPackages: Package[] = [];

    @observable
    totalPackagesInProject: number = 0;

    @observable
    currentPage: number = 0;

    @observable
    orderBy: OrderBy | undefined = undefined;

    @observable
    pageSize: number = LIST_PAGE_SIZE;

    @observable
    bulkDeleteInProcess: boolean = false;

    cache: CellMeasurerCache;

    private searchForAutocompleteCallServer: (s: string) => Promise<void>;

    private readonly localStorageService = new LocalStorageService();

    @computed
    get packages(): Package[] {
        return this.currentProjectPackages.filter(x =>
            this.isPackageSourceChecked ? x.source === PackageSource.Portal : x
        );
    }

    @computed
    get tags() {
        return this.projectStore.tags || [];
    }

    @computed
    get currentProject() {
        return this.rootStore.currentProject;
    }

    @computed
    get currentProjectFeatureFlags() {
        const project = this.projectStore.projects.find(p => p.id === this.currentProject?.id) ?? this.currentProject;
        return project?.storedFeatureFlags;
    }

    @computed
    get busyPackages() {
        if (this.packages) {
            const busyPackages = this.packages.filter(
                p => p.state !== PackageState.Ready && p.state !== PackageState.Broken
            );
            let packagesDictionary = {};
            busyPackages.forEach(p => {
                packagesDictionary[p.id] = p;
            });
            return packagesDictionary;
        }

        return {};
    }

    @computed
    get isGlobalReindexingInProcess() {
        return !!this.selectedPackages.find(x => !!this.isReindexingInProcess[x]);
    }

    @computed
    get isGlobalCleanupInProcess() {
        return !!this.selectedPackages.find(x => !!this.isCleanupInProcess[x]);
    }

    @computed
    get isProtectedPackageSelected() {
        return (
            this.selectedPackages.length > 0 &&
            this.selectedPackages.some(p => this.currentProjectPackages.find(x => x.id === p)?.isProtected)
        );
    }

    @computed
    get isUnprotectedPackageSelected() {
        return (
            this.selectedPackages.length > 0 &&
            this.selectedPackages.some(p => this.currentProjectPackages.find(x => x.id === p)?.isProtected !== true)
        );
    }

    @computed
    get isPackageWithApkgSelected() {
        return (
            this.selectedPackages.length > 0 &&
            this.selectedPackages.some(
                p =>
                    this.currentProjectPackages.find(x => x.id === p)?.featureFlags.general[
                        ALPHA_CONFIG_VARIABLES.OcrPackageUploadEnabled
                    ] === 'true'
            )
        );
    }

    @computed
    get selectionDownloadLabel() {
        if (!this.selectedPackages.length) {
            return '';
        }
        const names = this.selectedPackages
            .map(x => this.currentProjectPackages.find(y => y.id === x))
            .filter(x => x)
            .map(z => z!.name.split('.').pop());
        return names.every(x => x === names[0]) ? names[0] : 'originals';
    }

    @computed
    get protectedPackages() {
        return this.currentProjectPackages.filter(p => p.isProtected);
    }

    @computed
    get protectedPackageIds() {
        return this.protectedPackages.map(p => p.id);
    }

    @computed
    get protectedPackageIndexes() {
        return this.protectedPackages.map(p => this.currentProjectPackages.indexOf(p));
    }

    @computed
    get selectedPackageItems() {
        return this.currentProjectPackages.filter(p => this.selectedPackages.includes(p.id));
    }

    @computed
    get selectedPackageTags() {
        return [...new Set(_.flatMap(this.selectedPackageItems, p => p.userTags || []))].sort();
    }

    @computed
    get filtersModified() {
        return (
            this.pageSize !== defaultFilterValues.pageSize ||
            this.currentPage !== defaultFilterValues.currentPage ||
            this.searchTerm !== defaultFilterValues.searchTerm ||
            this.currentTags.length > 0 ||
            this.currentState.length > 0 ||
            this.isPackageSourceChecked !== defaultFilterValues.isPackageSourceChecked ||
            this.isProtectedOnlyChecked !== defaultFilterValues.isProtectedOnlyChecked ||
            this.orderBy !== defaultFilterValues.orderBy
        );
    }

    constructor(
        private rootStore: ProjectMetadataVisualStore,
        private service: ProjectsService,
        private projectStore: ProjectsRootVisualStore,
        private adminService: AdministrationService,
        private errorStore: ErrorStore,
        private globalAdministrationService: GlobalAdministrationService
    ) {
        this.projectStore.projectsStore.packageChanges.subscribe(p => {
            this.updatePackage(p);
        });

        reaction(
            () => this.currentProject,
            () => {
                if (!this.currentProject) {
                    return;
                }

                const storedFilters = this.localStorageService.getItem<PackageFilterStorageItem>(
                    this.getPackageFilterStorageKey(this.currentProject.id),
                    {}
                );

                runInAction(() => {
                    this.pageSize = storedFilters.pageSize ?? defaultFilterValues.pageSize;
                    this.currentPage = storedFilters.currentPage ?? defaultFilterValues.currentPage;
                    this.searchTerm = storedFilters.searchTerm ?? defaultFilterValues.searchTerm;
                    this.currentTags = storedFilters.currentTags ?? defaultFilterValues.currentTags;
                    this.currentState = storedFilters.currentState ?? defaultFilterValues.currentState;
                    this.isPackageSourceChecked =
                        storedFilters.isPackageSourceChecked ?? defaultFilterValues.isPackageSourceChecked;
                    this.isProtectedOnlyChecked =
                        storedFilters.isProtectedOnlyChecked ?? defaultFilterValues.isProtectedOnlyChecked;
                    this.orderBy = storedFilters.orderBy ?? defaultFilterValues.orderBy;
                });

                this.performSearch(this.searchTerm);
            }
        );

        autorun(() => {
            if (!this.currentProject) {
                return;
            }

            this.localStorageService.setItem<PackageFilterStorageItem>(
                this.getPackageFilterStorageKey(this.currentProject.id),
                {
                    pageSize: this.pageSize,
                    currentPage: this.currentPage,
                    searchTerm: this.searchTerm,
                    currentTags: this.currentTags,
                    currentState: this.currentState,
                    isPackageSourceChecked: this.isPackageSourceChecked,
                    isProtectedOnlyChecked: this.isProtectedOnlyChecked,
                    orderBy: this.orderBy
                }
            );
        });

        this.searchForAutocompleteCallServer = _.debounce(this.searchForAutocompleteImp, 200);

        this.fetchDefaultConfigValues();
    }

    @action.bound
    clearFilters() {
        runInAction(() => {
            this.pageSize = defaultFilterValues.pageSize;
            this.currentPage = defaultFilterValues.currentPage;
            this.searchTerm = defaultFilterValues.searchTerm;
            this.currentTags = defaultFilterValues.currentTags;
            this.currentState = defaultFilterValues.currentState;
            this.isPackageSourceChecked = defaultFilterValues.isPackageSourceChecked;
            this.isProtectedOnlyChecked = defaultFilterValues.isProtectedOnlyChecked;
            this.orderBy = defaultFilterValues.orderBy;
        });

        this.performSearch(this.searchTerm);
    }

    @action.bound
    setCurrentPage(page: number) {
        this.currentPage = page - 1;
        this.selectedPackages = [];
        this.loadProjectPackages(this.searchTerm);
    }

    @action.bound
    setOrderBy(orderBy: OrderBy | undefined) {
        this.orderBy = orderBy;
    }

    @action.bound
    setPageSize(pageSize?: number) {
        if (!pageSize) {
            return;
        }

        this.pageSize = pageSize;
    }

    @action.bound
    async fetchDefaultConfigValues() {
        const configs = await this.globalAdministrationService.getAlphaConfigs();
        let alphaConfigFlags: AlphaConfig[] = [];

        for (let conf of DefaultAlphaConfigurations) {
            const config = configs.find(x => x.name === conf.key);
            alphaConfigFlags.push({
                id: config ? config.id : conf.key,
                name: conf.key,
                value: config ? config.value : conf.defaultValue
            });
        }
        runInAction(() => {
            this.defaultAlphaConfigs = alphaConfigFlags;
        });
    }

    @action.bound
    setUploadDocsDialogVisible(visible: boolean) {
        this.uploadDocsDialogVisible = visible;
    }

    @action.bound
    setSearchTerm(pkgName: string) {
        this.searchTerm = pkgName;
    }
    @action.bound
    refreshCurrentProjectPackages() {
        this.currentProjectPackages = [...this.currentProjectPackages];
    }

    @action
    async searchForAutocomplete(term: string) {
        this.searchTerm = term;
        return this.searchForAutocompleteCallServer(this.searchTerm?.trim());
    }

    @action
    performSearch(term: string = '') {
        this.searchTerm = term;
        this.filterQuery = `${this.searchTerm}`;
        runInAction(() => {
            this.currentPage = 0;
            this.selectedPackages = [];
        });
        this.loadProjectPackages(this.searchTerm.trim());
    }

    @action.bound
    resetPackagesSelection() {
        this.selectedPackages = [];
    }

    @action.bound
    handlePackageSourceCheck(e: CheckboxChangeEvent) {
        this.isPackageSourceChecked = e.target.checked;
        this.performSearch(this.searchTerm);
    }

    @action.bound
    handleIsProtectedOnlyChecked(e: CheckboxChangeEvent) {
        this.isProtectedOnlyChecked = e.target.checked;
        this.performSearch(this.searchTerm);
    }

    @action.bound
    async handlePackagesReparse() {
        if (this.isSelectedProcessing) {
            message.error('Processing already in progress.');
            return;
        }
        const packageIds = [...this.selectedPackages];
        const project = this.currentProject;
        if (!project) {
            return;
        }
        const operationAtRequest = new Map(
            this.currentProjectPackages
                .filter(pkg => packageIds.includes(pkg.id))
                .map(pkg => [pkg.id, pkg.currentOperation])
        );
        try {
            runInAction(() => {
                packageIds.forEach(id => {
                    this.advanceProcessingRevision(id);
                    this.isStartingPreparation[id] = true;
                    this.isReparseInProcess[id] = true;
                });
                this.currentProjectPackages
                    .filter(x => packageIds.includes(x.id))
                    .forEach(p => p.updatePackageStateResult(PackageState.Busy));

                this.refreshCurrentProjectPackages();
            });

            const result = await this.adminService.parsePackagesBatch(packageIds);

            if (result.isErr()) {
                message.error(result.error.data?.title || result.error.text);
                await this.refreshProcessingPackages(project, packageIds, operationAtRequest);
                return;
            } else {
                this.resetPackagesSelection();
            }
        } catch (err) {
            message.error(err instanceof Error ? err.message : 'Unable to reprepare documents.');
            await this.refreshProcessingPackages(project, packageIds, operationAtRequest);
        } finally {
            runInAction(() => packageIds.forEach(id => (this.isStartingPreparation[id] = false)));
        }
    }

    private async refreshProcessingPackages(
        project: NonNullable<typeof this.currentProject>,
        packageIds: string[],
        operationAtRequest: ReadonlyMap<string, Package['currentOperation']>
    ) {
        const observed = new Map(
            this.currentProjectPackages
                .filter(pkg => packageIds.includes(pkg.id))
                .map(pkg => [
                    pkg.id,
                    {
                        operation: pkg.currentOperation,
                        state: pkg.serverState,
                        revision: this.processingRevisions.get(pkg.id)
                    }
                ])
        );
        try {
            const result = await this.service.searchPackages(project, {
                projectId: project.id,
                packageIds,
                pageSize: packageIds.length,
                page: 0,
                allSources: true
            });
            runInAction(() => {
                this.currentProjectPackages.forEach(pkg => {
                    const atRead = observed.get(pkg.id);
                    const current = result.lines.find(row => row.id === pkg.id);
                    if (
                        !atRead ||
                        !current ||
                        pkg.currentOperation !== operationAtRequest.get(pkg.id) ||
                        this.processingRevisions.get(pkg.id) !== atRead.revision ||
                        pkg.currentOperation !== atRead.operation ||
                        pkg.serverState !== atRead.state
                    ) {
                        return;
                    }
                    pkg.updatePackageStateResult(current.serverState);
                    pkg.currentOperation = current.currentOperation;
                    pkg.preparedSource = current.preparedSource;
                    pkg.publishedOutput = current.publishedOutput;
                    pkg.error = current.error;
                    pkg.errorDetails = current.errorDetails;
                    pkg.preparedSourceMatchesSettings = current.preparedSourceMatchesSettings;
                    this.isReparseInProcess[pkg.id] = false;
                    this.isProcessingStatusUnknown[pkg.id] = false;
                    this.advanceProcessingRevision(pkg.id);
                });
                this.refreshCurrentProjectPackages();
            });
        } catch {
            message.error('Refresh documents to check processing status.');
        }
    }

    @action.bound
    async handlePackagesReindex() {
        if (this.isSelectedProcessing) {
            message.error('Processing already in progress.');
            return;
        }
        const promises = this.selectedPackages.map(x => this.reindexPackage(x));
        this.resetPackagesSelection();
        await Promise.all(promises);
    }

    @action.bound
    async handlePackagesDelete() {
        const promises = this.selectedPackages.map(x => this.deletePackage(x));
        await Promise.all(promises);
        this.selectedPackages = [];
    }

    @action.bound
    async handlePackagesCleanup() {
        const promises = this.selectedPackages.map(x => this.cleanStorage(x));
        await Promise.all(promises);
    }

    @action.bound
    handleSelection(selectedKeys: []) {
        if (selectedKeys.length) {
            this.selectedPackages = this.currentProjectPackages.map(x => x.id);
        } else {
            this.selectedPackages = [];
        }
        // this.selectedPackages = selectedKeys;
    }

    @action
    async reparsePackage(packageId: string) {
        if (this.isPackageProcessing(packageId)) {
            message.error('Processing already in progress.');
            return;
        }
        const project = this.currentProject;
        if (!project) {
            return;
        }
        const operationAtRequest = new Map(
            this.currentProjectPackages.filter(pkg => pkg.id === packageId).map(pkg => [pkg.id, pkg.currentOperation])
        );
        this.advanceProcessingRevision(packageId);
        this.isReparseInProcess[packageId] = true;
        this.isReindexingInProcess[packageId] = true;
        this.isStartingPreparation[packageId] = true;
        this.currentProjectPackages.find(pkg => pkg.id === packageId)?.updatePackageStateResult(PackageState.Busy);
        try {
            const result = await this.adminService.parseByPackage(packageId);
            if (result.isErr()) {
                message.error(result.error.data?.title || result.error.text);
                await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
            }
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'Unable to reprepare document.');
            await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
        } finally {
            runInAction(() => {
                this.isReindexingInProcess[packageId] = false;
                this.isStartingPreparation[packageId] = false;
            });
        }
    }

    @action.bound
    async runInterpretation(packageId: string) {
        if (this.isPackageProcessing(packageId)) {
            message.error('Processing already in progress.');
            return;
        }
        if (!this.canRunInterpretation(packageId)) {
            message.error(
                'Reparse this document successfully before reprocessing; reusable OCR and previews could not be verified against the saved preparation settings.'
            );
            return;
        }

        const project = this.currentProject;
        if (!project) {
            return;
        }
        const operationAtRequest = new Map(
            this.currentProjectPackages.filter(pkg => pkg.id === packageId).map(pkg => [pkg.id, pkg.currentOperation])
        );
        this.advanceProcessingRevision(packageId);
        const revisionAtRequest = this.processingRevisions.get(packageId);
        this.isStartingInterpretation[packageId] = true;
        this.isProcessingStatusUnknown[packageId] = true;
        try {
            const result = await this.adminService.runInterpretation(packageId);
            if (result.isErr()) {
                message.error(result.error.data?.title || result.error.text);
                await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
                return;
            }
            runInAction(() => {
                const pkg =
                    this.currentProjectPackages.find(p => p.id === packageId) ?? this.processingRows.get(packageId);
                if (!pkg) {
                    return;
                }
                this.isProcessingStatusUnknown[packageId] = false;
                const current = result.value;
                // Notifications may already describe this operation's progress or a later operation.
                if (
                    this.processingRevisions.get(packageId) !== revisionAtRequest ||
                    (current.currentOperation?.id && pkg.currentOperation?.id === current.currentOperation.id)
                ) {
                    return;
                }
                pkg.updatePackageStateResult(
                    current.state === PackageStateResult.Ready
                        ? PackageState.Ready
                        : current.state === PackageStateResult.Broken
                          ? PackageState.Broken
                          : PackageState.Busy
                );
                pkg.currentOperation = current.currentOperation ?? null;
                pkg.preparedSource = current.preparedSource ?? null;
                pkg.publishedOutput = current.publishedOutput ?? null;
                pkg.error = current.error;
                pkg.errorDetails = current.errorDetails ?? null;
                pkg.preparedSourceMatchesSettings = current.preparedSourceMatchesSettings ?? null;
                this.advanceProcessingRevision(packageId);
            });
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'Unable to run Interpretation.');
            await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
        } finally {
            runInAction(() => (this.isStartingInterpretation[packageId] = false));
        }
    }

    @action
    async reindexPackage(packageId: string) {
        if (this.isPackageProcessing(packageId)) {
            message.error('Processing already in progress.');
            return;
        }
        this.isReindexingInProcess[packageId] = true;
        this.isProcessingStatusUnknown[packageId] = true;
        this.advanceProcessingRevision(packageId);
        const project = this.currentProject;
        const operationAtRequest = new Map(
            this.currentProjectPackages.filter(pkg => pkg.id === packageId).map(pkg => [pkg.id, pkg.currentOperation])
        );
        const pkgToUpdate = this.currentProjectPackages.find(p => p.id === packageId);
        pkgToUpdate?.updatePackageStateResult(PackageState.Busy);
        const projectPackageIndex = pkgToUpdate ? (project?.packages.indexOf(pkgToUpdate) ?? -1) : -1;
        if (projectPackageIndex >= 0) {
            this.projectStore.updatePackageStateInCurrentProject(projectPackageIndex, PackageState.Busy);
        }
        try {
            const result = await this.adminService.reindexByPackage(packageId);
            if (result.isErr()) {
                message.error(result.error.data?.title || result.error.text);
                if (project) {
                    await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
                }
            }
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'Unable to re-index document.');
            if (project) {
                await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
            }
        } finally {
            runInAction(() => (this.isReindexingInProcess[packageId] = false));
        }
    }

    @action.bound
    async updatePackagesProtection(packageIds: string[], isProtected: boolean) {
        const pkgsToUpdate = this.currentProjectPackages.filter(p => packageIds.includes(p.id));
        if (pkgsToUpdate.length === 0) {
            return;
        }

        const resp = await this.adminService.updatePackagesProtection(packageIds, isProtected);

        if (resp.isOk()) {
            pkgsToUpdate.forEach(p => p.updatePackageProtection(isProtected));
            message.success('Packages protection updated');
        } else {
            message.error('Failed to update packages protection');
        }
    }

    @action.bound
    async deletePackage(id: string) {
        const pkg = this.currentProjectPackages.find(p => p.id === id)!;
        if (pkg.operationState && _.includes(pkg.operationState, 'UserReviewed')) {
            message.warning("Can't delete current package as it has labels linked to it");
            return;
        }
        const operationAtRequest = new Map([[id, pkg.currentOperation]]);
        const revisionAtRequest = this.processingRevisions.get(id);
        const resp = await this.adminService.deletePackage(id);
        if (resp.isOk()) {
            runInAction(() => {
                if (this.searchTerm) {
                    this.autocompleteSource = this.autocompleteSource.filter(a => a.filePath !== pkg.documentPath);
                }
                this.selectedPackages = this.selectedPackages.filter(p => p !== id);
            });
            resp.map(() => this.removePackageFromList(id));
            message.success(`Deleted package '${pkg.name}' successfully`);
        } else {
            message.error(resp.error.data?.title || resp.error.text || 'Failed to delete package');
            if (this.currentProject && resp.error.status === 409) {
                runInAction(() => {
                    if (this.processingRevisions.get(id) === revisionAtRequest) {
                        this.isProcessingStatusUnknown[id] = true;
                        this.advanceProcessingRevision(id);
                    }
                });
                await this.refreshProcessingPackages(this.currentProject, [id], operationAtRequest);
            }
        }
    }

    @action
    async bulkDeletePackagesByDateRange(startDate: string, endDate: string) {
        try {
            if (!this.currentProject) {
                return;
            }

            this.bulkDeleteInProcess = true;

            message.success('Deletion of packages has started');

            const resp = await this.adminService.bulkDeletePackagesByDateRange(
                this.currentProject.id,
                startDate,
                endDate
            );

            if (resp.isOk()) {
                message.success('Packages deleted successfully');
                this.loadProjectPackages(this.searchTerm);
            } else {
                message.error('Failed to delete packages');
            }
        } finally {
            this.bulkDeleteInProcess = false;
        }
    }

    @action.bound
    removePackageFromList(id: string) {
        this.currentProjectPackages = this.currentProjectPackages.filter(p => p.id !== id);
    }

    @action
    async cleanStorage(packageId: string) {
        this.isCleanupInProcess[packageId] = true;
        await this.adminService.cleanStorage(packageId);
        runInAction(() => (this.isCleanupInProcess[packageId] = false));
    }

    @action.bound
    setTags(tags: string[]) {
        this.currentTags = tags;
        this.performSearch(this.searchTerm);
    }

    @action.bound
    setState(state: string[]) {
        this.currentState = state;
        this.selectedPackages = [];
    }

    @action.bound
    async updatePackageUserTags(pkg: Package, tags: string[]) {
        try {
            await this.service.updatePackageUserTags(pkg, tags);
            await this.projectStore.fetchTags();

            runInAction(() => {
                if (this.projectStore.currentProject) {
                    var pkgInProj = this.projectStore.currentProject.packages.find(p => p.id === pkg.id);
                    if (pkgInProj) {
                        pkgInProj.userTags = tags;
                    }
                }
            });
        } catch (error) {
            this.errorStore.addBasicError(error);
        }
    }

    @action.bound
    async updateSelectedPackagesTags(tagsToAdd: string[], tagsToRemove: string[]) {
        try {
            const packagesToUpdate = this.selectedPackageItems;

            if (!packagesToUpdate.length) {
                return false;
            }

            const updates = packagesToUpdate
                .map(pkg => {
                    const nextTags = [...new Set([...(pkg.userTags || []), ...tagsToAdd])].filter(
                        tag => !tagsToRemove.includes(tag)
                    );

                    return { pkg, nextTags };
                })
                .filter(({ pkg, nextTags }) => !_.isEqual(pkg.userTags || [], nextTags));

            if (!updates.length) {
                return true;
            }

            type SettledResult =
                | { status: 'fulfilled'; pkg: Package; nextTags: string[] }
                | { status: 'rejected'; reason: unknown };

            const results: SettledResult[] = await Promise.all(
                updates.map(({ pkg, nextTags }) =>
                    this.service
                        .updatePackageUserTags(pkg, nextTags)
                        .then((): SettledResult => ({ status: 'fulfilled', pkg, nextTags }))
                        .catch((reason: unknown): SettledResult => ({ status: 'rejected', reason }))
                )
            );

            const succeeded = results.filter(
                (r): r is SettledResult & { status: 'fulfilled' } => r.status === 'fulfilled'
            );
            const failed = results.filter((r): r is SettledResult & { status: 'rejected' } => r.status === 'rejected');

            if (succeeded.length) {
                await this.projectStore.fetchTags();

                runInAction(() => {
                    succeeded.forEach(({ pkg, nextTags }) => {
                        pkg.setUserTags(nextTags);
                        this.projectStore.currentProject?.packages.find(p => p.id === pkg.id)?.setUserTags(nextTags);
                    });
                });
            }

            if (failed.length) {
                message.error(`Failed to update tags for ${failed.length} package(s)`);
                failed.forEach(r => this.errorStore.addBasicError(r.reason as Error));
            }

            if (succeeded.length) {
                message.success(
                    failed.length
                        ? `Tags updated for ${succeeded.length} of ${updates.length} package(s)`
                        : 'Package tags updated'
                );
            }

            return failed.length === 0;
        } catch (error) {
            this.errorStore.addBasicError(error);
            return false;
        }
    }

    @action.bound
    handlePackageSelection(e: CheckboxChangeEvent, packageId: string) {
        if (e.nativeEvent.shiftKey) {
            this.handleMultipleSelection(e.target.checked, packageId);
            return;
        }

        if (e.target.checked) {
            this.selectedPackages.push(packageId);
        } else {
            const index = this.selectedPackages.indexOf(packageId);
            this.selectedPackages.splice(index, 1);
        }
    }

    @action.bound
    handleMultipleSelection(selected: boolean, packageId: string) {
        const packageIds = this.currentProjectPackages.map(p => p.id);
        const indexOfCurrentPackage = packageIds.indexOf(packageId);
        const indexOfLastSelectedPackage = packageIds.indexOf(this.selectedPackages[this.selectedPackages.length - 1]);
        let packagesToChange: string[] = [];

        if (indexOfCurrentPackage > indexOfLastSelectedPackage) {
            packagesToChange = packageIds.slice(indexOfLastSelectedPackage, indexOfCurrentPackage + 1);
        } else {
            packagesToChange = packageIds.slice(indexOfCurrentPackage, indexOfLastSelectedPackage + 1);
        }

        if (selected) {
            this.selectedPackages = [...new Set(this.selectedPackages.concat(packagesToChange))];
        } else {
            this.selectedPackages = this.selectedPackages.filter(p => !packagesToChange.includes(p));
        }
    }

    @action.bound
    async updatePackageFeatureFlags(packageId: string, featureFlags: Record<string, string> | null) {
        const project = this.currentProject;
        const pkg = this.currentProjectPackages.find(p => p.id === packageId);
        if (!project || !pkg) {
            message.error('Package not found');
            return false;
        }
        if (this.isPackageProcessing(packageId)) {
            message.error('Processing already in progress.');
            return false;
        }
        const operationAtRequest = new Map([[packageId, pkg.currentOperation]]);
        this.advanceProcessingRevision(packageId);
        this.isStartingPreparation[packageId] = true;
        this.isProcessingStatusUnknown[packageId] = true;
        try {
            const resp = await this.service.updatePackageFeatureFlags(packageId, featureFlags);

            if (resp.isErr()) {
                message.error(resp.error.data?.title || resp.error.text || 'Failed to update package feature flags');
                await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
                return false;
            }

            message.success('Package feature flags saved. Reparsing the document and generating previews.');
            runInAction(() => (this.isStartingPreparation[packageId] = false));
            await this.loadProjectPackages(this.searchTerm);
            return true;
        } catch (error) {
            this.errorStore.addBasicError(error);
            await this.refreshProcessingPackages(project, [packageId], operationAtRequest);
            return false;
        } finally {
            runInAction(() => (this.isStartingPreparation[packageId] = false));
        }
    }

    @action.bound
    async exportPackageFeautureFlags(packageId: string) {
        await this.service.exportPackageFeautureFlags(packageId);
    }

    @action.bound
    async loadProjectPackages(term: string | null = null) {
        const request = ++this.packageListRequest;
        const project = this.currentProject;
        const revisionsAtRead = new Map(this.processingRevisions);
        const pendingAtRead = new Set(
            [...this.processingRevisions.keys()].filter(id => this.isProcessingRequestPending(id))
        );
        try {
            runInAction(() => {
                this.isLoading = true;
            });
            const result = await this.searchInProject(term);
            if (request !== this.packageListRequest || project !== this.currentProject) return;

            runInAction(() => {
                this.currentProjectPackages = result.lines.map(pkg => {
                    const current =
                        this.currentProjectPackages.find(row => row.id === pkg.id) ?? this.processingRows.get(pkg.id);
                    if (
                        this.processingRevisions.get(pkg.id) !== revisionsAtRead.get(pkg.id) ||
                        pendingAtRead.has(pkg.id) ||
                        this.isProcessingRequestPending(pkg.id)
                    ) {
                        if (!current) this.isProcessingStatusUnknown[pkg.id] = true;
                        return current ?? pkg;
                    }
                    this.isProcessingStatusUnknown[pkg.id] = false;
                    if (pkg.serverState === PackageState.Ready || pkg.serverState === PackageState.Broken) {
                        this.resetUiState(pkg.id);
                    }
                    this.processingRows.delete(pkg.id);
                    return pkg;
                });
                this.totalPackagesInProject = result.total;
            });
        } catch (err) {
            message.error('Error loading packages');
            console.error(err);
        } finally {
            runInAction(() => {
                if (request === this.packageListRequest) this.isLoading = false;
            });
        }
    }

    @action
    private async searchForAutocompleteImp(term: string) {
        const request = {
            projectId: this.rootStore.currentProject!.id,
            search: term,
            page: 0,
            pageSize: 15,
            allSources: this.isPackageSourceChecked ? false : true,
            orderBy: this.orderBy,
            uploadedBy: this.projectStore.hasAccessToAllEntities ? null : this.projectStore.currentUserId
        };

        const result = await this.service.searchPackages(this.rootStore.currentProject!, request);

        if (!result) {
            runInAction(() => {
                this.autocompleteSource = [];
            });
            return;
        }

        runInAction(() => {
            this.autocompleteSource = result.lines.map(l => ({
                fileName: l.name,
                filePath: l.documentPath!,
                state: l.state
            }));
        });
    }

    @action.bound
    private updatePackage(pkgChanges: PackageChange) {
        if (!this.currentProject || pkgChanges.projectId !== this.currentProject.id) {
            return;
        }
        this.advanceProcessingRevision(pkgChanges.id);
        this.isProcessingStatusUnknown[pkgChanges.id] = false;

        const pkgToUpdate =
            this.currentProjectPackages.find(p => p.id === pkgChanges.id) ?? this.processingRows.get(pkgChanges.id);
        if (!pkgToUpdate && this.packageChangesShouldBeInserted(pkgChanges)) {
            const newPkg = new Package(
                this.rootStore.currentProject!,
                pkgChanges.id,
                pkgChanges.fileName,
                'application/pdf',
                pkgChanges.state,
                pkgChanges.filePath,
                [],
                pkgChanges.userTags,
                pkgChanges.source,
                pkgChanges.uploadDate,
                pkgChanges.indexDate,
                pkgChanges.error,
                pkgChanges.featureFlags,
                pkgChanges.isProtected,
                pkgChanges.currentOperation ?? null,
                pkgChanges.preparedSource ?? null,
                pkgChanges.publishedOutput ?? null,
                pkgChanges.errorDetails ?? null,
                pkgChanges.preparedSourceMatchesSettings ?? null
            );

            let newPackages = [...this.currentProjectPackages];
            newPackages.unshift(newPkg);
            this.currentProjectPackages = newPackages;
        } else {
            if (pkgToUpdate) {
                // Package snapshots omit null fields, so absent flags mean they were cleared.
                pkgToUpdate.updateFeatureFlags(pkgChanges.featureFlags ?? null);
                pkgToUpdate.currentOperation = pkgChanges.currentOperation ?? null;
                pkgToUpdate.preparedSource = pkgChanges.preparedSource ?? null;
                pkgToUpdate.publishedOutput = pkgChanges.publishedOutput ?? null;
                pkgToUpdate.error = pkgChanges.error;
                pkgToUpdate.errorDetails = pkgChanges.errorDetails ?? null;
                pkgToUpdate.preparedSourceMatchesSettings = pkgChanges.preparedSourceMatchesSettings ?? null;
            }

            this.updateStateOfExistingPackage(pkgChanges.id, pkgChanges.state);
            if (pkgChanges.state === PackageState.Ready || pkgChanges.state === PackageState.Broken) {
                this.resetUiState(pkgChanges.id);
                this.projectStore.reloadCurrentProject();
                this.loadProjectPackages(this.searchTerm);
            }
        }
    }

    @action.bound
    private updateStateOfExistingPackage(packageId: string, state: PackageState) {
        const pkgToUpdate =
            this.currentProjectPackages.find(p => p.id === packageId) ?? this.processingRows.get(packageId);
        if (!pkgToUpdate) {
            return;
        }

        let newPackages = [...this.currentProjectPackages];
        pkgToUpdate.updatePackageStateResult(state);
        this.currentProjectPackages = newPackages;
    }

    @action
    private resetUiState(id: string) {
        this.isReparseInProcess[id] = false;
    }

    isPackageProcessing(id: string) {
        const pkg = this.currentProjectPackages.find(p => p.id === id) ?? this.processingRows.get(id);
        return Boolean(
            this.isStartingInterpretation[id] ||
                this.isProcessingStatusUnknown[id] ||
                this.isStartingPreparation[id] ||
                this.isReparseInProcess[id] ||
                this.isReindexingInProcess[id] ||
                pkg?.currentOperation?.active ||
                pkg?.serverState === PackageState.Busy
        );
    }

    hasVerifiedPreparedSource(id: string) {
        const source = this.currentProjectPackages.find(p => p.id === id)?.preparedSource;
        return Boolean(source?.generation?.trim() && source.fieldSetId?.trim() && source.artifactPath?.trim());
    }

    canRunInterpretation(id: string) {
        const pkg = this.currentProjectPackages.find(p => p.id === id);
        return Boolean(
            pkg &&
                (pkg.serverState === PackageState.Ready || pkg.serverState === PackageState.Broken) &&
                this.hasVerifiedPreparedSource(id) &&
                pkg.preparedSourceMatchesSettings === true &&
                !this.isPackageProcessing(id)
        );
    }

    @computed
    get isSelectedProcessing() {
        return this.selectedPackages.some(id => this.isPackageProcessing(id));
    }

    handlePackagesDownload(fileType: 'pdf' | 'apkg') {
        if (this.selectedPackages.length === 1) {
            return this.service.handleDownload(this.selectedPackages[0], fileType);
        }
        return this.service.handleArchiveDownload(this.selectedPackages, fileType);
    }

    async getPackageCountByDateRange(startDate: string, endDate: string) {
        return this.currentProject
            ? await this.adminService.getPackageCountByDateRange(this.currentProject.id, startDate, endDate)
            : 0;
    }

    private packageChangesShouldBeInserted(pkgChanges: PackageChange) {
        const includedInTags = pkgChanges.userTags.some(t => this.currentTags.includes(t)) || !this.currentTags.length;
        const nameSatisfiesCriteria =
            pkgChanges.fileName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            !this.searchTerm ||
            this.searchTerm.trim() === '';
        const sourceIsPortal = this.isPackageSourceChecked ? pkgChanges.source === PackageSource.Portal : true;
        return includedInTags && nameSatisfiesCriteria && sourceIsPortal;
    }

    private searchInProject(term: string | null): Promise<PackagesResponse> {
        if (!this.rootStore.currentProject) {
            return Promise.resolve({ lines: [], total: 0 });
        }

        const request = {
            projectId: this.rootStore.currentProject!.id,
            search: term,
            page: this.currentPage,
            pageSize: this.pageSize,
            tags: this.currentTags,
            allSources: this.isPackageSourceChecked ? false : true,
            orderBy: this.orderBy,
            state: this.currentState,
            protectedOnly: this.isProtectedOnlyChecked,
            uploadedBy: this.projectStore.hasAccessToAllEntities ? null : this.projectStore.currentUserId
        };

        return this.service.searchPackages(this.rootStore.currentProject!, request);
    }

    private getPackageFilterStorageKey(projectId: string) {
        return `package-filter-${projectId}-v${packageFilterStorageVersion}`;
    }
}

export default UploadedPackagesVisualStore;
